import type { CenterFilters } from './centerReports';
import { SCREENS, type ViewKind } from './nav';
import type { Tab as ReportsTab } from './Reports';

/** الشاشة الحالية ومعاملاتها. أسماؤها وأيقوناتها ومجموعاتها فى `nav.ts`. */
export type View =
  | { kind: 'dashboard' }
  | { kind: 'journal' }
  | { kind: 'ledger'; accountCode: string | null }
  | { kind: 'periods' }
  | { kind: 'chart' }
  | { kind: 'reports'; tab?: ReportsTab }
  | { kind: 'reportsCenter'; initial?: Partial<CenterFilters> }
  | { kind: 'customers'; customerId?: string }
  | { kind: 'invoices'; query?: string; customerId?: string }
  | { kind: 'catalog'; query?: string }
  | { kind: 'inventory' }
  | { kind: 'receipts'; query?: string; customerId?: string }
  | { kind: 'suppliers'; supplierId?: string }
  | { kind: 'bills'; query?: string; supplierId?: string }
  | { kind: 'payments'; query?: string; supplierId?: string }
  | { kind: 'treasury' }
  | { kind: 'expenses' }
  | { kind: 'treasuryAccounts' }
  | { kind: 'reconciliation'; bankAccountId?: string }
  | { kind: 'settings'; tab?: SettingsTab }
  | { kind: 'setup' }
  | { kind: 'migration' }
  | { kind: 'legacy' }
  | { kind: 'platform' };

export type SettingsTab = 'company' | 'einvoice' | 'members' | 'password';

/**
 * لكل شاشة رابط: الرجوع والتقدّم فى المتصفح يتنقلان بين الشاشات، والتحديث يبقى فى نفس
 * الشاشة، والرابط يُرسل أو يُفتح فى تبويب جديد. المسار بالإنجليزى (يُكتب ويُقرأ فى أى لوحة
 * مفاتيح)، والمعرّفات كما هى. ما لا يُفهم يعود للرئيسية — لا صفحة «غير موجود».
 */
const SEGMENTS: Record<ViewKind, string> = {
  dashboard: '',
  journal: 'journal',
  ledger: 'ledger',
  periods: 'periods',
  chart: 'chart-of-accounts',
  reports: 'statements',
  reportsCenter: 'reports',
  customers: 'customers',
  invoices: 'invoices',
  catalog: 'items',
  inventory: 'inventory',
  receipts: 'receipts',
  suppliers: 'suppliers',
  bills: 'bills',
  payments: 'payments',
  treasury: 'treasury',
  expenses: 'expenses',
  treasuryAccounts: 'cash-and-banks',
  reconciliation: 'reconciliation',
  settings: 'settings',
  setup: 'setup',
  migration: 'migration',
  legacy: 'archive',
  platform: 'platform',
};

const KIND_OF = new Map<string, ViewKind>(
  (Object.entries(SEGMENTS) as [ViewKind, string][]).map(([kind, segment]) => [segment, kind]),
);

const REPORT_TABS: readonly ReportsTab[] = [
  'income',
  'balance',
  'general',
  'aging',
  'apAging',
  'vat',
  'withholding',
];
const SETTINGS_TABS: readonly SettingsTab[] = ['company', 'einvoice', 'members', 'password'];

/** معرّف من الرابط: uuid أو كود حساب بأرقام — غيره يُتجاهل فلا يصل نص غريب لطلب. */
const ID = /^[0-9a-f-]{36}$/i;
const CODE = /^[0-9]{1,20}$/;

function withQuery(path: string, params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) search.set(key, value);
  const text = search.toString();
  return text ? path + '?' + text : path;
}

export function pathOf(view: View): string {
  const base = '/' + SEGMENTS[view.kind];
  switch (view.kind) {
    case 'ledger':
      return view.accountCode ? base + '/' + view.accountCode : base;
    case 'customers':
      return view.customerId ? base + '/' + view.customerId : base;
    case 'suppliers':
      return view.supplierId ? base + '/' + view.supplierId : base;
    case 'reconciliation':
      return view.bankAccountId ? base + '/' + view.bankAccountId : base;
    case 'settings':
      return view.tab && view.tab !== 'company' ? base + '/' + view.tab : base;
    case 'reports':
      return view.tab && view.tab !== 'income' ? base + '/' + view.tab : base;
    case 'invoices':
    case 'receipts':
      return withQuery(base, { q: view.query, customer: view.customerId });
    case 'bills':
    case 'payments':
      return withQuery(base, { q: view.query, supplier: view.supplierId });
    case 'catalog':
      return withQuery(base, { q: view.query });
    case 'reportsCenter': {
      const params: Record<string, string | undefined> = {};
      for (const [key, value] of Object.entries(view.initial ?? {}))
        params[key] = value === undefined ? undefined : String(value);
      return withQuery(base, params);
    }
    default:
      return base;
  }
}

export function viewOf(pathname: string, search = ''): View {
  const [segment = '', id] = pathname.replace(/^\/+|\/+$/g, '').split('/');
  const kind = KIND_OF.get(decodeURIComponent(segment));
  const params = new URLSearchParams(search);
  const q = params.get('q')?.slice(0, 100) || undefined;
  const uuid = (value: string | null | undefined) => (value && ID.test(value) ? value : undefined);
  switch (kind) {
    case undefined:
    case 'dashboard':
      return { kind: 'dashboard' };
    case 'ledger':
      return { kind: 'ledger', accountCode: id && CODE.test(id) ? id : null };
    case 'customers':
      return uuid(id) ? { kind: 'customers', customerId: id } : { kind: 'customers' };
    case 'suppliers':
      return uuid(id) ? { kind: 'suppliers', supplierId: id } : { kind: 'suppliers' };
    case 'reconciliation':
      return uuid(id) ? { kind: 'reconciliation', bankAccountId: id } : { kind: 'reconciliation' };
    case 'settings':
      return SETTINGS_TABS.includes(id as SettingsTab)
        ? { kind: 'settings', tab: id as SettingsTab }
        : { kind: 'settings' };
    case 'reports':
      return REPORT_TABS.includes(id as ReportsTab)
        ? { kind: 'reports', tab: id as ReportsTab }
        : { kind: 'reports' };
    case 'invoices':
    case 'receipts':
      return clean({ kind, query: q, customerId: uuid(params.get('customer')) });
    case 'bills':
    case 'payments':
      return clean({ kind, query: q, supplierId: uuid(params.get('supplier')) });
    case 'catalog':
      return clean({ kind, query: q });
    case 'reportsCenter': {
      const initial: Record<string, string | boolean> = {};
      for (const [key, value] of params) {
        if (key.length > 40 || value.length > 100) continue;
        initial[key] = value === 'true' ? true : value === 'false' ? false : value;
      }
      return Object.keys(initial).length
        ? { kind, initial: initial as Partial<CenterFilters> }
        : { kind };
    }
    default:
      return { kind } as View;
  }
}

/** بلا مفاتيح فاضية — فالرابط والعرض يتطابقان ذهابًا وإيابًا. */
function clean<T extends object>(view: T): T {
  return Object.fromEntries(Object.entries(view).filter(([, value]) => value !== undefined)) as T;
}

/** نفس الشاشة بنفس المعاملات — لا يُضاف للتاريخ رجوع لنفس المكان. */
export function sameView(a: View, b: View): boolean {
  return pathOf(a) === pathOf(b);
}

/**
 * الأب فى مسار التنقل: كشف العميل أبوه قائمة العملاء، والفاتورة المتفلترة أبوها كل
 * الفواتير. الشاشة العليا أبوها null — ولا زرار رجوع ولا مسار لها.
 */
export function parentOf(view: View): View | null {
  switch (view.kind) {
    case 'customers':
      return view.customerId ? { kind: 'customers' } : null;
    case 'suppliers':
      return view.supplierId ? { kind: 'suppliers' } : null;
    case 'reconciliation':
      return view.bankAccountId ? { kind: 'treasuryAccounts' } : null;
    case 'invoices':
    case 'receipts':
    case 'bills':
    case 'payments':
    case 'catalog':
      return Object.keys(view).length > 1 ? { kind: view.kind } : null;
    case 'settings':
      return view.tab && view.tab !== 'company' ? { kind: 'settings' } : null;
    default:
      return null;
  }
}

/** اسم الخطوة الأخيرة فى المسار لشاشة فرعية — الأب يأخذ اسمه من `nav.ts`. */
export function leafLabel(view: View): string | null {
  switch (view.kind) {
    case 'customers':
      return view.customerId ? 'كشف حساب العميل' : null;
    case 'suppliers':
      return view.supplierId ? 'كشف حساب المورد' : null;
    case 'reconciliation':
      return view.bankAccountId ? 'مطابقة الحساب' : null;
    case 'invoices':
    case 'receipts':
    case 'bills':
    case 'payments':
    case 'catalog':
      if (view.query) return 'نتائج «' + view.query + '»';
      if ('customerId' in view && view.customerId) return 'لعميل محدد';
      if ('supplierId' in view && view.supplierId) return 'لمورد محدد';
      return null;
    case 'settings':
      return view.tab && view.tab !== 'company' ? SETTINGS_LABELS[view.tab] : null;
    default:
      return null;
  }
}

const SETTINGS_LABELS: Record<SettingsTab, string> = {
  company: 'بيانات الشركة',
  einvoice: 'الفاتورة الإلكترونية',
  members: 'المستخدمون',
  password: 'حسابى',
};

/** اسم الشاشة من خريطة القائمة — للمسار وعنوان التبويب. */
export function screenTitle(view: View): string {
  return SCREENS[view.kind].title;
}

/** اختصارات Alt+رقم لأكثر الشاشات استعمالاً — بالرقم لا بالحرف، فتعمل مع الكيبورد العربى. */
export const SHORTCUTS: readonly { key: string; kind: ViewKind }[] = [
  { key: '1', kind: 'dashboard' },
  { key: '2', kind: 'invoices' },
  { key: '3', kind: 'receipts' },
  { key: '4', kind: 'customers' },
  { key: '5', kind: 'bills' },
  { key: '6', kind: 'treasury' },
  { key: '7', kind: 'reportsCenter' },
];
